import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { 
  UserGroupIcon, 
  AcademicCapIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  UserIcon,
  BuildingOffice2Icon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { getEmployeesCompetencies } from "../../../../api/hse/competency";
import { fetchDepartments } from "../../../../api/department";
import { toast } from "react-hot-toast";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Employees - Employee competency overview (management only)
 * Only visible to admin/manager - shows all employees' competencies
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyEmployees = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    departments: [],
  });
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });
  const canViewAll = isAdmin || canManage;

  useEffect(() => {
    if (canViewAll) {
      fetchDepartmentsList();
      fetchAllEmployees();
    }
  }, [canViewAll, filters, sortBy, sortOrder]);

  const fetchDepartmentsList = async () => {
    try {
      const response = await fetchDepartments({});
      // Ensure we always have an array
      let departmentsList = [];
      if (Array.isArray(response)) {
        departmentsList = response;
      } else if (response?.departments && Array.isArray(response.departments)) {
        departmentsList = response.departments;
      } else if (response?.data && Array.isArray(response.data)) {
        departmentsList = response.data;
      }
      setDepartments(departmentsList);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setDepartments([]); // Set to empty array on error
    }
  };

  const fetchAllEmployees = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        search: filters.query,
        department: filters.departments.length > 0 ? filters.departments[0] : undefined,
      };
      
      const response = await getEmployeesCompetencies(apiFilters);
      setEmployees(response.employees || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError(true);
      toast.error(t("Kunne ikke laste ansattoversikt"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      departments: newFilters.departments || [],
    });
  };

  // Department options for ModuleFilter
  const departmentOptions = Array.isArray(departments) ? departments.map(dept => ({
    value: dept.id,
    label: dept.name,
  })) : [];

  // Custom dropdown filters
  const customDropdownFilters = [
    {
      key: "departments",
      label: t("Avdeling"),
      icon: BuildingOffice2Icon,
      options: departmentOptions.map(dept => ({
        value: dept.value,
        label: dept.label,
        class: "bg-blue-100 text-blue-800",
      })),
    },
  ];

  // If user doesn't have access, redirect to own view
  if (!canViewAll) {
    navigate("/dashboard/hse-ks/competency");
    return null;
  }

  const sortedEmployees = [...employees].sort((a, b) => {
    let aVal = a[sortBy] || "";
    let bVal = b[sortBy] || "";
    
    if (sortBy === "name") {
      aVal = (a.name || a.email || "").toLowerCase();
      bVal = (b.name || b.email || "").toLowerCase();
    }
    
    if (sortOrder === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste ansattoversikt")]} />;
  }

  return (
    <HsePageChrome
      title={t("Ansattoversikt")}
      subtitle={t("Oversikt over ansattes kompetanse (ledelse)")}
      backButton={{
        label: t("Tilbake til kompetanse"),
        action: () => navigate("/dashboard/hse-ks/competency"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <CompetencySubnav />
          {/* Info Box if no employees */}
          {employees.length === 0 && !loading && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-yellow-900 mb-2">
                {t("Ingen ansatte funnet")}
              </h3>
              <p className="text-sm text-yellow-700">
                {t("Det ser ut til at det ikke er registrert ansatte i systemet ennå. Kontakt administrator for å legge til ansatte.")}
              </p>
            </div>
          )}
          
          {/* Summary Stats */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Totalt ansatte")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{employees.length}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-8 w-8 text-green-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Alle gyldige")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {employees.reduce((sum, e) => sum + (e.competencies_count || 0), 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-8 w-8 text-yellow-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Utløper snart")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {employees.reduce((sum, e) => sum + (e.expiring_soon_count || 0), 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Utløpte")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {employees.reduce((sum, e) => sum + (e.expired_count || 0), 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i ansatte...")}
            customDropdownFilters={customDropdownFilters}
          />

          {/* Employees List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Alle ansatte")}</h3>
              
              {sortedEmployees.length === 0 ? (
                <ResourceEmpty message={t("Ingen ansatte funnet")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("name")}
                        >
                          <div className="flex items-center">
                            {t("Ansatt")}
                            {sortBy === "name" && (
                              <span className="ml-2 text-gray-400">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Avdeling")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Kompetanser")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {sortedEmployees.map((employee) => (
                        <tr
                          key={employee.id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() =>
                            navigate(`/dashboard/hse-ks/competency/employees/${employee.id}`)
                          }
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                  <UserIcon className="h-6 w-6 text-indigo-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="font-medium text-gray-900">
                                  {employee.name || employee.email}
                                </div>
                                {employee.email && employee.name && (
                                  <div className="text-gray-500 text-xs">{employee.email}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {employee.department_name || employee.department ? (
                              <span className="flex items-center">
                                <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                                {employee.department_name || employee.department}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <AcademicCapIcon className="h-4 w-4 mr-1 text-blue-600" />
                              {employee.competencies_count || 0}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="flex flex-col space-y-1">
                              {employee.expiring_soon_count > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <ClockIcon className="h-3 w-3 mr-1" />
                                  {employee.expiring_soon_count} {t("utløper snart")}
                                </span>
                              )}
                              {employee.expired_count > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                  {employee.expired_count} {t("utløpt")}
                                </span>
                              )}
                              {employee.expiring_soon_count === 0 && employee.expired_count === 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  {t("OK")}
                                </span>
                              )}
                            </div>
                          </td>
                          <td
                            className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="flex items-center justify-end space-x-2">
                              <Link
                                to={`/dashboard/hse-ks/competency/employees/${employee.id}`}
                                className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                {t("Vis")}
                              </Link>
                              {canManage ? (
                                <Link
                                  to={`/dashboard/hse-ks/competency/employees/${employee.id}/assign`}
                                  className="text-gray-600 hover:text-gray-900 inline-flex items-center"
                                >
                                  <PencilIcon className="h-4 w-4 mr-1" />
                                  {t("Rediger")}
                                </Link>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

